<template>
    <!-- Bar variant: the left half of the mobile floating CTA. Tapping the
         price scrolls to the availability calendar (the date-picking surface);
         scrollIntoView honours #availability's scroll-mt offset. -->
    <!-- Caption ABOVE the figure, not beside it: with the band folded into one
         line ("Weekends from £2,853") the price itself truncated on a 390px
         phone — cutting off the one thing the bar exists to show. Split in two,
         only the caption can ever clip. -->
    <a
        v-if="variant === 'bar' && price"
        href="#availability"
        class="flex flex-col gap-1 min-w-0 text-left"
        @click.prevent="scrollToAvailability"
    >
        <span
            v-if="caption"
            class="text-xsss text-brand-2-950/70 leading-none truncate"
            v-text="caption"
        ></span>

        <span
            class="font-semibold text-brand-2-950 text-sm leading-none truncate"
            v-text="price"
        ></span>
    </a>

    <!-- Inline variant: one more item in the form's "2 nights · Not available"
         line, carrying its own separator and inheriting that line's type so the
         price reads as part of the same sentence rather than a second block
         repeating the dates. -->
    <template v-else-if="variant === 'inline' && quoted">
        <span
            aria-hidden="true"
            class="text-brand-2-950/40"
        >·</span>

        <span v-text="labelledQuote"></span>
    </template>

    <!-- Panel variant: one row in the desktop price card for the chosen dates.
         Single line at a single size — dates, nights, then the figure — so it
         reads the same way as the form's "9 nights · From £3,500 · Not
         available" line rather than stacking a label over a bigger number. No
         "Your dates" prefix: the dates name themselves, and the row sits under
         the published bands where nothing else could be meant. Renders only when
         the chosen dates are actually quotable. -->
    <!-- Flex, not plain inline text: as flex items the line breaks BETWEEN the
         parts if the panel ever forces a wrap — never leaving "9" above "nights"
         or "From" above "£3,500". The gap supplies the spacing that Vue's
         whitespace condensing strips between elements.
         Sized at the dates' own scale (14/16px, not the price scale's 18px) —
         one size for the whole row, dates included. -->
    <p
        v-else-if="variant === 'panel' && quoted"
        class="flex flex-wrap items-baseline gap-x-1.5 text-xsss sm:text-xs font-semibold text-brand-2-950"
    >
        <!-- The dot travels with the dates, not the price: on a long stay this row
             wraps at panel width, and a dot opening the second line reads as a
             bullet. Trailing, it reads as "continues below". Same dot the caption
             puts between the dates and the nights, so all three parts are
             punctuated alike. -->
        <span class="inline-flex items-baseline gap-x-1.5">
            <span v-text="caption"></span>

            <!-- No opacity on this one: the caption's own dates·nights dot is plain
                 text at full strength, so a muted second dot reads as a different
                 mark. (The inline variant's dot IS muted — it matches the form
                 line's other separators, which are muted too.) -->
            <span aria-hidden="true">·</span>
        </span>

        <span v-text="labelledQuote"></span>
    </p>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';
    import axios from 'axios';

    import { enquiryDates, nightsBetween, formatShortRange } from '@js/src/stores/enquiryDates';

    /**
     * Module-level so every instance on the page shares it — the page mounts this
     * component up to three times (floating bar, under the calendar, in the
     * enquiry block). Storing the in-flight PROMISE, not just the result, means
     * simultaneous mounts asking for the same dates make one request between
     * them instead of one each.
     */
    const priceRequests = new Map();

    function loadPriceSets(propertyId, from, to) {
        const key = `${propertyId}|${from}|${to}`;

        if (!priceRequests.has(key)) {
            priceRequests.set(
                key,
                axios.get(`/wp-json/lh/v1/properties/${propertyId}/price-checker`, {
                    params: { from, to },
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': Page.nonce,
                    },
                })
                    .then((response) => response.data?.price_sets || null)
                    // Quiet failure by design: the published bands still show.
                    .catch(() => null),
            );
        }

        return priceRequests.get(key);
    }

    const props = defineProps({
        propertyId: { type: [Number, String], required: true },
        variant: { type: String, default: 'bar' },
        // The published bands, pre-formatted server-side as a RANGE where the
        // owner set both bounds ("£2,853 – £4,100") and "from £2,853" where they
        // only set a floor. Never a bare floor: on its own it reads as the price.
        weekendTypical: { type: String, default: '' },
        weekTypical: { type: String, default: '' },
    });

    /**
     * Whether a published band's rate genuinely covers a stay of `nights`.
     *
     * PropertyPriceMatrix buckets by "days" (nights + 1) and sends anything from
     * 4 nights upwards to the week band, which would quote a 4-night stay at the
     * 7-night rate. The weekend bands price an exact length; the week rate is
     * treated as covering 6 nights and up — close enough to a week that owners
     * charge it, and it also keeps 8+ night stays quoted rather than silently
     * unpriced. 4 and 5 nights fall between the theme's published rates, so
     * they aren't quoted at all.
     *
     * A SINGLE weekend night takes the two-night weekend rate: that's what owners
     * charge for it, the theme publishes no one-night rate, and the server has
     * already returned the two-night band for it (isOverWeekend + days <= 3). A
     * single mid-week night comes back as mid_week with a notice, so it stays
     * unquoted.
     */
    const BAND_COVERS = {
        two_night_weekend: (nights, overWeekend) => nights === 2 || (nights === 1 && overWeekend),
        three_night_weekend: (nights) => nights === 3,
        week: (nights) => nights >= 6,
    };

    /**
     * What a band is called to a guest. The server's own labels ("Two night
     * weekend", "Full week") read as headings; these read inside a sentence,
     * after the word "Typical".
     */
    const BAND_LABELS = {
        two_night_weekend: '2-night weekend',
        three_night_weekend: '3-night weekend',
        week: 'week',
    };

    const priceSets = ref(null);
    let debounceTimer = null;

    const nights = computed(() => nightsBetween(enquiryDates.from, enquiryDates.to));

    const hasDates = computed(() => Boolean(enquiryDates.from && enquiryDates.to && nights.value));

    // Mirrors PropertyPriceMatrix::isOverWeekend() — Fri, Sat or Sun on either
    // end of the stay. ISO weekday numbering (Mon 1 … Sun 7) so the >= 5 test
    // reads the same on both sides of the wire.
    const overWeekend = computed(() => {
        const isoWeekday = (value) => {
            const date = new Date(`${value}T00:00:00`);

            if (Number.isNaN(date.getTime())) {
                return 0;
            }

            return date.getDay() === 0 ? 7 : date.getDay();
        };

        return isoWeekday(enquiryDates.from) >= 5 || isoWeekday(enquiryDates.to) >= 5;
    });

    /**
     * What the theme can say about the CHOSEN dates, or null when it can say
     * nothing. Empty is common and expected — mid-week stays and any stay of
     * 4-6 nights have no published rate at all.
     *
     * `exact` is the distinction that matters, and it is why this returns an
     * object rather than a string. An exact figure is a price the OWNER set for
     * these dates and is the only true quote the site can make. A band is a
     * seasonal average that knows nothing about the dates a guest just picked.
     *
     * Showing the band's floor as "From £1,600" against chosen dates read as a
     * quote for those dates and cost an owner a day of unpicking it with a guest
     * who had screenshotted the number (Clift Hill, Nov 2026 - guest saw
     * £1,600, owner's real rate was £2,400). "From" does not survive being put
     * next to the dates someone chose; only the range and the word "typical" do.
     */
    const quote = computed(() => {
        if (!hasDates.value || !priceSets.value) {
            return null;
        }

        const usable = Object.entries(priceSets.value).filter(([, set]) => {
            return set && !set.notice && set.amount && !/(^|\s)£0(\s|$)/.test(set.amount);
        });

        // A single figure means an owner set this price for these exact dates (a
        // special offer or a dated specific price), so it applies whatever the
        // stay's shape. A range is a published band, and only applies when the
        // stay is the length that band prices.
        const exact = usable.find(([, set]) => !set.amount.includes(' - '));

        if (exact) {
            return { amount: exact[1].amount, exact: true, band: '' };
        }

        const band = usable.find(([key]) => BAND_COVERS[key]?.(nights.value, overWeekend.value));

        if (!band) {
            return null;
        }

        // The whole range, en dash, never the floor alone. Where an owner filled
        // in only the lower bound there is no range to show, so the wording has
        // to carry it instead.
        const [floor, ceiling] = band[1].amount.split(' - ');

        return {
            amount: ceiling ? `${floor} – ${ceiling}` : `from ${floor}`,
            exact: false,
            band: BAND_LABELS[band[0]] || '',
        };
    });

    const quoted = computed(() => quote.value?.amount || '');

    const quoteIsExact = computed(() => Boolean(quote.value?.exact));

    /**
     * The figure, carrying one word when it is a band rather than a price the
     * owner set for these dates.
     *
     * One word, in the same line, is the whole qualification. Both fuller
     * versions this replaced ("Not a quote for your dates - the owner will
     * confirm", then "For an accurate quote please send an enquiry") sat on a
     * second line under the number, which reads as a disclaimer attached to the
     * price and gives a guest two lines to interpret where one will do. It also
     * travels better: a screenshot of the number cannot leave the word behind,
     * and the enquiry form is already the next thing on the page.
     *
     * Capitalised because these lines are rows of separate items rather than
     * sentences - "2 nights", "Available" and this all carry a capital.
     *
     * An exact figure is never labelled: it IS the guest's price, and that
     * contrast is the point.
     */
    const labelledQuote = computed(() => {
        if (!quoted.value) {
            return '';
        }

        return quoteIsExact.value ? quoted.value : `Typically ${quoted.value}`;
    });

    // What the figure below actually refers to — the chosen stay when it's
    // quotable, otherwise the published band. Naming the band is the point: a
    // bare "£2,853" tells a guest nothing about what it buys.
    const caption = computed(() => {
        // The bar shows the figure alone, with no room to label it in place, so
        // there the caption carries the word instead - naming the band rather
        // than the guest's dates, which is what stops the figure reading as
        // theirs. Same job as "Typically" on the other two, done a line up.
        if (quote.value && !quote.value.exact && props.variant === 'bar') {
            return quote.value.band ? `Typical ${quote.value.band}` : 'Typical rate';
        }

        if (quoted.value) {
            return `${formatShortRange(enquiryDates.from, enquiryDates.to)} · ${nights.value} night${nights.value === 1 ? '' : 's'}`;
        }

        if (props.weekendTypical) {
            return 'Typical weekend';
        }

        return props.weekTypical ? 'Typical week' : '';
    });

    // Unquotable dates fall back to the published band and make no claim about
    // those dates — never "Enquire for price", which would replace a real number
    // with a non-number the moment a guest engaged with the calendar.
    const price = computed(() => {
        return quoted.value || props.weekendTypical || props.weekTypical || '';
    });

    function scrollToAvailability() {
        const target = document.querySelector('#availability') || document.querySelector('#enquiry-block');

        target?.scrollIntoView({ behavior: 'smooth' });
    }

    function fetchPrice(from, to) {
        loadPriceSets(props.propertyId, from, to).then((sets) => {
            // Ignore a resolution for dates the guest has since changed away from.
            if (enquiryDates.from === from && enquiryDates.to === to) {
                priceSets.value = sets;
            }
        });
    }

    watch(
        () => [enquiryDates.from, enquiryDates.to],
        () => {
            clearTimeout(debounceTimer);

            if (!hasDates.value) {
                priceSets.value = null;

                return;
            }

            debounceTimer = setTimeout(() => fetchPrice(enquiryDates.from, enquiryDates.to), 250);
        },
        { immediate: true },
    );
</script>
